import { useState, useEffect, useMemo } from "react";
import DateInput from "@/components/DateInput";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Bank, MagnifyingGlass, Plus, ArrowSquareOut,
  XCircle, Warning, ArrowUUpLeft, PencilSimple, Trash, CaretUp, CaretDown, CaretUpDown,
  CaretLeft, CaretRight, FileXls,
} from "@phosphor-icons/react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import BancoCombobox from "@/components/BancoCombobox";
import ProveedorCombobox from "@/components/ProveedorCombobox";
import NumberInput from "@/components/ui/NumberInput";

const fmtMoney = (n, m = "ARS") =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: m, minimumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "-";
  const s = String(iso);
  // Si es solo fecha YYYY-MM-DD, parsear como local a mediodía para que no
  // corra un día por la zona horaria (problema clásico UTC vs AR/UTC-3).
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d, 12).toLocaleDateString("es-AR");
  }
  return new Date(s).toLocaleDateString("es-AR");
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const ESTADO_BADGE = {
  aceptado:   { label: "Aceptado",    className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  depositado: { label: "Depositado",  className: "bg-blue-50 text-blue-700 border-blue-200" },
  anulado:    { label: "Anulado",     className: "bg-neutral-100 text-neutral-500 border-neutral-300" },
  rechazado:  { label: "Rechazado",   className: "bg-red-50 text-red-700 border-red-200" },
  rescatado:  { label: "Rescatado",   className: "bg-amber-50 text-amber-700 border-amber-200" },
};

// ── FirmanteSelect ──────────────────────────────────────────────────────────
// Dropdown con firmantes existentes + opción "+ Nuevo firmante". Cada ítem
// tiene botones inline de editar/eliminar al hover. Al seleccionar "nuevo",
// abre un mini-input inline para tipear el nombre y persiste vía POST.
function FirmanteSelect({ value, onChange, firmantes, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [saving, setSaving] = useState(false);
  const [editando, setEditando] = useState(null); // {nombreActual, nombreNuevo}
  const [dropOpen, setDropOpen] = useState(false);

  const opciones = useMemo(() => {
    const arr = (firmantes || []).map((f) => f.nombre);
    if (value && !arr.includes(value)) arr.unshift(value);
    return arr;
  }, [firmantes, value]);

  const crear = async () => {
    const nombre = nuevoNombre.trim();
    if (!nombre) return;
    setSaving(true);
    try {
      await api.post("/cheques-emitidos/firmantes", { nombre });
      onChange(nombre);
      setNuevoNombre("");
      setCreating(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el firmante");
    } finally {
      setSaving(false);
    }
  };

  const guardarEdit = async () => {
    if (!editando) return;
    const nuevo = (editando.nombreNuevo || "").trim();
    if (!nuevo) return;
    if (nuevo === editando.nombreActual) { setEditando(null); return; }
    setSaving(true);
    try {
      await api.put("/cheques-emitidos/firmantes", {
        nombre_actual: editando.nombreActual, nombre_nuevo: nuevo,
      });
      toast.success("Firmante renombrado");
      if (value === editando.nombreActual) onChange(nuevo);
      setEditando(null);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo renombrar");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (nombre) => {
    if (!confirm(`¿Eliminar firmante "${nombre}"?`)) return;
    try {
      await api.delete(`/cheques-emitidos/firmantes/${encodeURIComponent(nombre)}`);
      toast.success("Firmante eliminado");
      if (value === nombre) onChange("");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  if (creating) {
    return (
      <div className="flex gap-1" data-testid="firmante-crear-inline">
        <Input autoFocus value={nuevoNombre}
               onChange={(e) => setNuevoNombre(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); crear(); } }}
               placeholder="Nombre del nuevo firmante"
               className="rounded-sm h-9 text-[13px] font-mono flex-1"
               data-testid="firmante-nuevo-input" />
        <Button type="button" size="sm" className="bg-[#921A0E] hover:bg-[#7a1509] text-white h-9"
                onClick={crear} disabled={saving || !nuevoNombre.trim()}
                data-testid="firmante-nuevo-guardar">
          {saving ? "…" : "Crear"}
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-9"
                onClick={() => { setCreating(false); setNuevoNombre(""); }}>×</Button>
      </div>
    );
  }

  if (editando) {
    return (
      <div className="flex gap-1" data-testid="firmante-editar-inline">
        <Input autoFocus value={editando.nombreNuevo}
               onChange={(e) => setEditando({ ...editando, nombreNuevo: e.target.value })}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); guardarEdit(); } }}
               className="rounded-sm h-9 text-[13px] font-mono flex-1"
               data-testid="firmante-editar-input" />
        <Button type="button" size="sm" className="bg-[#921A0E] hover:bg-[#7a1509] text-white h-9"
                onClick={guardarEdit} disabled={saving}
                data-testid="firmante-editar-guardar">
          {saving ? "…" : "Guardar"}
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-9"
                onClick={() => setEditando(null)}>×</Button>
      </div>
    );
  }

  return (
    <Select
      value={value || ""}
      open={dropOpen}
      onOpenChange={setDropOpen}
      onValueChange={(v) => {
        if (v === "__new__") { setCreating(true); return; }
        onChange(v);
      }}
    >
      <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="firmante-select">
        <SelectValue placeholder="Elegí o creá un firmante…" />
      </SelectTrigger>
      <SelectContent>
        {opciones.length === 0 && (
          <div className="px-2 py-1.5 text-[11px] text-neutral-500 font-mono">
            No hay firmantes cargados.
          </div>
        )}
        {opciones.map((n) => (
          <div key={n} className="group relative flex items-center hover:bg-neutral-100 rounded-sm">
            <SelectItem value={n} className="flex-1 pr-16">{n}</SelectItem>
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 z-10">
              <button
                type="button"
                className="p-1 rounded-sm hover:bg-blue-100 text-blue-600"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  setEditando({ nombreActual: n, nombreNuevo: n });
                  setDropOpen(false);
                }}
                title="Renombrar firmante"
                data-testid={`firmante-editar-${n}`}
              >
                <PencilSimple size={12} />
              </button>
              <button
                type="button"
                className="p-1 rounded-sm hover:bg-red-100 text-red-600"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  eliminar(n);
                  setDropOpen(false);
                }}
                title="Eliminar firmante"
                data-testid={`firmante-eliminar-${n}`}
              >
                <Trash size={12} />
              </button>
            </div>
          </div>
        ))}
        <SelectItem value="__new__" className="text-[#921A0E] font-semibold border-t mt-1">
          + Nuevo firmante…
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

// ── Modal EMITIR / EDITAR ───────────────────────────────────────────────────
function EmitirChequeDialog({ open, onOpenChange, onSaved, edit }) {
  const isEdit = !!edit;
  const [proveedores, setProveedores] = useState([]);
  const [cajasBanco, setCajasBanco] = useState([]);
  const [padron, setPadron] = useState(null);
  const [firmantes, setFirmantes] = useState([]);
  const reloadFirmantes = async () => {
    try {
      const { data } = await api.get("/cheques-emitidos/firmantes");
      setFirmantes(data || []);
    } catch (e) {}
  };

  const [form, setForm] = useState({
    proveedor_id: "",
    fecha: todayISO(),
    monto: "",
    moneda: "ARS",
    concepto: "",
    // cheque
    numero: "",
    banco: null,               // {code, name} elegido en BancoCombobox
    caja_origen_id: "",
    fecha_emision: todayISO(),
    fecha_cobro: "",
    firmante: "",
    observaciones: "",
  });
  const [facturasPend, setFacturasPend] = useState([]);
  const [facturasSel, setFacturasSel] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: p }, { data: c }, { data: pd }, { data: fs }] = await Promise.all([
          api.get("/proveedores"),
          api.get("/cajas"),
          api.get("/cheques-emitidos/padron-emisor"),
          api.get("/cheques-emitidos/firmantes"),
        ]);
        setProveedores(p || []);
        setCajasBanco((c || []).filter((x) => x.tipo === "banco"));
        setPadron(pd || null);
        setFirmantes(fs || []);
      } catch (e) {
        toast.error("No se pudo cargar catálogos");
      }
    })();
  }, [open]);

  // Prefill en modo edición
  useEffect(() => {
    if (!open) return;
    if (edit) {
      setForm({
        proveedor_id: edit.proveedor_id || "",
        fecha: edit.fecha_entrega || edit.fecha_emision || todayISO(),
        monto: String(edit.monto || ""),
        moneda: edit.moneda || "ARS",
        concepto: "",
        numero: edit.numero || "",
        banco: edit.banco_code ? { code: edit.banco_code, name: edit.banco_nombre } : null,
        caja_origen_id: edit.caja_origen_id || "",
        fecha_emision: edit.fecha_emision || todayISO(),
        fecha_cobro: edit.fecha_cobro || "",
        firmante: edit.firmante || "",
        observaciones: edit.observaciones || "",
      });
    } else {
      setForm({
        proveedor_id: "", fecha: todayISO(), monto: "", moneda: "ARS", concepto: "",
        numero: "", banco: null, caja_origen_id: "",
        fecha_emision: todayISO(), fecha_cobro: "",
        firmante: "", observaciones: "",
      });
      setFacturasSel(new Set());
    }
  }, [open, edit]);

  // Facturas pendientes del proveedor (solo en modo nuevo)
  useEffect(() => {
    if (isEdit) return;
    if (!form.proveedor_id) { setFacturasPend([]); setFacturasSel(new Set()); return; }
    (async () => {
      try {
        const { data } = await api.get("/compras", {
          params: { contraparte_id: form.proveedor_id },
        });
        setFacturasPend((data || []).filter((f) => (f.saldo || 0) > 0.001 && f.estado !== "anulada"));
      } catch (e) {
        setFacturasPend([]);
      }
    })();
  }, [form.proveedor_id, isEdit]);

  const total = parseFloat(form.monto) || 0;
  const totalSel = useMemo(() =>
    facturasPend.filter((f) => facturasSel.has(f.id)).reduce((s, f) => s + (f.saldo || 0), 0),
  [facturasPend, facturasSel]);

  const toggleFactura = (fid) => {
    const s = new Set(facturasSel);
    if (s.has(fid)) s.delete(fid); else s.add(fid);
    setFacturasSel(s);
  };

  const submit = async () => {
    if (!isEdit && !form.proveedor_id) return toast.error("Seleccioná un proveedor");
    if (!form.numero) return toast.error("Ingresá el número de cheque");
    if (!form.banco?.code) return toast.error("Elegí el banco de la chequera");
    if (!form.caja_origen_id) return toast.error("Elegí la caja / banco de origen");
    if (!form.fecha_cobro) return toast.error("Ingresá la fecha de pago");
    if (!isEdit && total <= 0) return toast.error("El monto debe ser mayor a 0");

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/cheques-emitidos/${edit.id}`, {
          numero: form.numero,
          banco_code: form.banco.code,
          banco_nombre: form.banco.name,
          caja_origen_id: form.caja_origen_id,
          fecha_emision: form.fecha_emision,
          fecha_cobro: form.fecha_cobro,
          firmante: form.firmante || null,
          observaciones: form.observaciones || null,
        });
        toast.success(`Cheque N° ${form.numero} actualizado`);
      } else {
        const payload = {
          proveedor_id: form.proveedor_id,
          fecha: form.fecha,
          monto: total,
          moneda: form.moneda,
          concepto: form.concepto || null,
          facturas_aplicadas: [...facturasSel],
          cheque: {
            numero: form.numero,
            banco_code: form.banco.code,
            banco_nombre: form.banco.name,
            caja_origen_id: form.caja_origen_id,
            fecha_emision: form.fecha_emision,
            fecha_cobro: form.fecha_cobro,
            firmante: form.firmante || null,
            cuit_emisor: (padron?.cuit || "30-71849121-1"),
            razon_social_emisor: padron?.razon_social || null,
            observaciones: form.observaciones || null,
          },
        };
        await api.post("/cheques-emitidos", payload);
        toast.success(`Cheque N° ${form.numero} emitido`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No se pudo ${isEdit ? "editar" : "emitir"} el cheque`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="che-emit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={20} weight="duotone" className="text-[#921A0E]" />
            {isEdit ? `Editar Cheque N° ${edit?.numero || ""}` : "Emitir Cheque a Proveedor"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-[13px]">
          {/* Datos del emisor (auto Padrón AFIP) */}
          <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-800 mb-1">
              Emisor (auto Padrón AFIP)
            </div>
            {padron?.razon_social ? (
              <>
                <div className="font-semibold">{padron.razon_social}</div>
                <div className="text-[11px] text-neutral-600 font-mono">
                  CUIT {padron.cuit} · {padron.condicion_iva || "—"} · {padron.direccion || ""}
                </div>
              </>
            ) : (
              <div className="text-[11px] text-neutral-600">
                CUIT 30-71849121-1 {padron?.error ? `· ${padron.error}` : "(cargando datos de Padrón…)"}
              </div>
            )}
          </div>

          {/* Proveedor + Monto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">
                Proveedor {isEdit && <span className="text-amber-600">(no editable — anular y re-emitir para cambiarlo)</span>}
              </Label>
              {isEdit ? (
                <div className="h-9 px-2 flex items-center border border-neutral-200 rounded-sm text-[12px] bg-neutral-50 truncate">
                  {(proveedores.find((p) => p.id === form.proveedor_id) || {}).nombre || "—"}
                </div>
              ) : (
                <ProveedorCombobox
                  proveedores={proveedores}
                  value={form.proveedor_id}
                  onChange={(v) => setForm({ ...form, proveedor_id: v })}
                  placeholder="Buscar proveedor por nombre, CUIT…"
                  testId="che-emit-proveedor"
                />
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">
                  Monto {isEdit && <span className="text-amber-600">(no editable)</span>}
                </Label>
                <NumberInput step="0.01" value={form.monto}
                  onChange={(e) => setForm({ ...form, monto: e.target.value })}
                  placeholder="0.00" className="rounded-sm h-9 text-[13px] font-mono"
                  data-testid="che-emit-monto"
                  disabled={isEdit}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Moneda</Label>
                <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}
                        disabled={isEdit}>
                  <SelectTrigger className="rounded-sm h-9 text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Facturas pendientes (solo modo Nuevo) */}
          {!isEdit && form.proveedor_id && facturasPend.length > 0 && (
            <div className="border border-neutral-200 rounded-sm p-2 bg-white">
              <div className="text-[10px] uppercase tracking-widest font-mono text-neutral-500 mb-1">
                Aplicar a facturas pendientes (opcional; si no elegís, se aplica FIFO)
              </div>
              <div className="max-h-40 overflow-y-auto">
                {facturasPend.map((f) => (
                  <label key={f.id}
                         className="flex items-center gap-2 py-1 hover:bg-neutral-50 text-[12px] font-mono">
                    <input type="checkbox" checked={facturasSel.has(f.id)}
                           onChange={() => toggleFactura(f.id)}
                           data-testid={`che-emit-fac-${f.numero || f.id}`} />
                    <span className="font-semibold">{f.tipo_comprobante} {f.numero}</span>
                    <span className="text-neutral-500">· {fmtDate(f.fecha)}</span>
                    <span className="ml-auto">Saldo: {fmtMoney(f.saldo, f.moneda)}</span>
                  </label>
                ))}
              </div>
              {totalSel > 0 && (
                <div className="text-[11px] mt-1 pt-1 border-t border-neutral-200 font-mono">
                  Total facturas seleccionadas: <b>{fmtMoney(totalSel)}</b>
                  {total !== 0 && Math.abs(totalSel - total) > 0.01 && (
                    <span className="text-amber-700 ml-2">
                      (diferencia con el cheque: {fmtMoney(total - totalSel)})
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Datos del cheque */}
          <div className="border border-neutral-200 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest font-mono text-neutral-500 mb-2">
              Datos del cheque
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Banco (chequera propia)</Label>
                <BancoCombobox
                  value={form.banco}
                  onChange={(b) => setForm({ ...form, banco: b })}
                  testId="che-emit-banco"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Caja / Banco de origen</Label>
                <Select value={form.caja_origen_id} onValueChange={(v) => setForm({ ...form, caja_origen_id: v })}>
                  <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="che-emit-caja">
                    <SelectValue placeholder="Elegir caja bancaria…" />
                  </SelectTrigger>
                  <SelectContent>
                    {cajasBanco.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre} <span className="text-neutral-500 ml-1">({fmtMoney(c.saldo || 0, c.moneda)})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">N° de cheque</Label>
                <Input value={form.numero}
                       onChange={(e) => setForm({ ...form, numero: e.target.value })}
                       placeholder="00012345"
                       className="rounded-sm h-9 text-[13px] font-mono"
                       data-testid="che-emit-numero" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Firmante</Label>
                <FirmanteSelect
                  value={form.firmante}
                  onChange={(v) => setForm({ ...form, firmante: v })}
                  firmantes={firmantes}
                  onCreated={reloadFirmantes}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Fecha de emisión</Label>
                <DateInput value={form.fecha_emision}
                       onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })}
                       className="rounded-sm h-9 text-[13px] font-mono" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Fecha de pago</Label>
                <DateInput value={form.fecha_cobro}
                       onChange={(e) => setForm({ ...form, fecha_cobro: e.target.value })}
                       className="rounded-sm h-9 text-[13px] font-mono"
                       data-testid="che-emit-fecha-cobro" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Observaciones</Label>
                <Textarea value={form.observaciones}
                          onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                          placeholder="Notas internas del cheque…"
                          className="rounded-sm text-[12px] font-mono min-h-[60px]" />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button className="bg-[#921A0E] hover:bg-[#7a1509] text-white"
                  onClick={submit} disabled={saving}
                  data-testid="che-emit-submit">
            {saving ? (isEdit ? "Guardando…" : "Emitiendo…") : (isEdit ? "Guardar cambios" : "Emitir cheque")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Modal Acción (depositar/anular/rechazar/rescatar) ───────────────────────
function AccionDialog({ cheque, accion, onOpenChange, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [fecha, setFecha] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  useEffect(() => { setMotivo(""); setFecha(todayISO()); }, [cheque, accion]);

  if (!cheque || !accion) return null;
  const CFG = {
    depositar: { titulo: "Depositar cheque",
                 desc: `Debita el banco ${cheque.caja_origen_nombre} y marca el cheque como cobrado por el proveedor.`,
                 btn: "Depositar", color: "bg-blue-600 hover:bg-blue-700",
                 icon: ArrowSquareOut, needsFecha: true },
    anular:    { titulo: "Anular cheque",
                 desc: "Revierte la operación completa: el saldo del proveedor y las facturas vuelven al estado previo.",
                 btn: "Anular",     color: "bg-red-600 hover:bg-red-700",
                 icon: XCircle,     needsFecha: false },
    rechazar:  { titulo: "Rechazar cheque (rebotó)",
                 desc: `Re-acredita el banco ${cheque.caja_origen_nombre}, vuelve la caja "Cheques emitidos" al pasivo y devuelve el saldo pendiente al proveedor.`,
                 btn: "Rechazar",   color: "bg-red-600 hover:bg-red-700",
                 icon: Warning,     needsFecha: true },
    rescatar:  { titulo: "Rescatar cheque",
                 desc: "Le devolvés el cheque al proveedor y le pagarás con otro medio. Se revierte la operación pero queda diferenciado del anulado.",
                 btn: "Rescatar",   color: "bg-amber-600 hover:bg-amber-700",
                 icon: ArrowUUpLeft, needsFecha: false },
  }[accion];
  const IconEl = CFG.icon;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/cheques-emitidos/${cheque.id}/${accion}`, {
        motivo: motivo || null, fecha: CFG.needsFecha ? fecha : null,
      });
      toast.success(`Cheque ${accion === "depositar" ? "depositado" : accion + "do"}`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No se pudo ${accion}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!cheque} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`che-accion-${accion}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconEl size={20} weight="duotone" />
            {CFG.titulo}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[13px]">
          <p>
            Cheque <b>N° {cheque.numero}</b> · {cheque.banco_nombre} ·{" "}
            {fmtMoney(cheque.monto, cheque.moneda)} → {cheque.proveedor_nombre}
          </p>
          <p className="text-neutral-600 text-[11px]">{CFG.desc}</p>
          {CFG.needsFecha && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Fecha</Label>
              <DateInput value={fecha} onChange={(e) => setFecha(e.target.value)}
                     className="rounded-sm h-9 text-[13px] font-mono" />
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Motivo / Observación (opcional)</Label>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)}
                      placeholder="Ej: Rebotó por falta de fondos"
                      className="rounded-sm text-[12px] font-mono min-h-[60px]"
                      data-testid={`che-accion-motivo-${accion}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button className={`text-white ${CFG.color}`} onClick={submit} disabled={saving}
                  data-testid={`che-accion-submit-${accion}`}>
            {saving ? "Procesando…" : CFG.btn}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Página principal ───────────────────────────────────────────────────────
export default function ChequesEmitidosPage() {
  const [cheques, setCheques] = useState([]);
  const [stats, setStats] = useState({
    aceptado:   { count: 0, total: 0 },
    depositado: { count: 0, total: 0 },
    anulado:    { count: 0, total: 0 },
    rechazado:  { count: 0, total: 0 },
    rescatado:  { count: 0, total: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [filterEstado, setFilterEstado] = useState("__all__");
  const [filterQ, setFilterQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [accion, setAccion] = useState(null);
  const [target, setTarget] = useState(null);
  const [filterFirmante, setFilterFirmante] = useState("__all__");
  const [firmantesLista, setFirmantesLista] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cheques-emitidos/firmantes");
        setFirmantesLista(data || []);
      } catch (e) {}
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterEstado !== "__all__") params.estado = filterEstado;
      const [{ data: chs }, { data: st }] = await Promise.all([
        api.get("/cheques-emitidos", { params }),
        api.get("/cheques-emitidos/stats"),
      ]);
      setCheques(chs || []);
      setStats(st || stats);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterEstado]);

  const [sort, setSort] = useState({ key: "fecha_emision", dir: "desc" });
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "monto" ? "desc" : "asc" }));
  const SortIcon = ({ col }) => sort.key !== col
    ? <CaretUpDown size={11} className="text-neutral-400" />
    : sort.dir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />;

  const filtered = useMemo(() => {
    let arr = cheques;
    if (filterFirmante !== "__all__") {
      arr = arr.filter((c) => (c.firmante || "") === filterFirmante);
    }
    if (filterQ) {
      const q = filterQ.toLowerCase();
      arr = arr.filter((c) =>
        (c.numero || "").toLowerCase().includes(q) ||
        (c.banco_nombre || "").toLowerCase().includes(q) ||
        (c.proveedor_nombre || "").toLowerCase().includes(q) ||
        (c.firmante || "").toLowerCase().includes(q)
      );
    }
    const mul = sort.dir === "asc" ? 1 : -1;
    return [...arr].sort((a, b) => {
      const va = sort.key === "monto" ? Number(a.monto || 0) : (a[sort.key] || "");
      const vb = sort.key === "monto" ? Number(b.monto || 0) : (b[sort.key] || "");
      return va < vb ? -mul : va > vb ? mul : 0;
    });
  }, [cheques, filterQ, filterFirmante, sort]);

  const [pageSize, setPageSize] = useState(20); // 20 | 50 | 0 (todos)
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filterQ, filterEstado, filterFirmante, pageSize, sort]);
  const totalPages = pageSize ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const pageRows = pageSize ? filtered.slice((page - 1) * pageSize, page * pageSize) : filtered;

  const exportExcel = () => {
    const rows = filtered.map((c) => ({
      "Nº": c.numero,
      "Banco": `${c.banco_code || ""} ${c.banco_nombre || ""}`.trim(),
      "Caja origen": c.caja_origen_nombre || "",
      "Proveedor": c.proveedor_nombre || "",
      "Firmante": c.firmante || "",
      "F. Emisión": fmtDate(c.fecha_emision),
      "F. Pago": fmtDate(c.fecha_cobro),
      "Monto": Number(c.monto || 0),
      "Moneda": c.moneda || "ARS",
      "Estado": ESTADO_BADGE[c.estado]?.label || c.estado,
      "Motivo": c.motivo || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cheques emitidos");
    const suf = filterEstado !== "__all__" ? `_${filterEstado}` : "";
    XLSX.writeFile(wb, `ChequesEmitidos${suf}_${todayISO()}.xlsx`);
  };

  const abrirAccion = (ch, acc) => { setTarget(ch); setAccion(acc); };
  const cerrarAccion = () => { setTarget(null); setAccion(null); };

  return (
    <div className="space-y-6" data-testid="cheques-emitidos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading flex items-center gap-2">
            <Bank size={28} weight="duotone" className="text-[#921A0E]" />
            Cheques Emitidos
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportExcel} variant="outline" size="sm" className="rounded-sm"
                  disabled={filtered.length === 0}
                  data-testid="che-emit-export-excel">
            <FileXls size={13} weight="bold" className="mr-1.5" />
            Descargar Excel
          </Button>
          <Button onClick={() => setOpenNew(true)} size="sm"
                  className="bg-[#921A0E] hover:bg-[#7a1509] text-white rounded-sm"
                  data-testid="che-emit-nuevo">
            <Plus size={13} weight="bold" className="mr-1.5" />
            Emitir cheque
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {[
          ["aceptado",   "Vigentes",   "text-emerald-700"],
          ["depositado", "Depositados","text-blue-700"],
          ["rechazado",  "Rechazados", "text-red-700"],
          ["rescatado",  "Rescatados", "text-amber-700"],
          ["anulado",    "Anulados",   "text-neutral-500"],
        ].map(([est, lbl, cls]) => {
          const active = filterEstado === est;
          return (
          <Card key={est}
                role="button" tabIndex={0}
                onClick={() => setFilterEstado(active ? "__all__" : est)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFilterEstado(active ? "__all__" : est); } }}
                title={active ? "Quitar filtro" : `Ver sólo ${lbl.toLowerCase()}`}
                className={`border cursor-pointer select-none transition-colors ${active ? "ring-2 ring-[#921A0E] border-[#921A0E] bg-[#FFF7F5]" : "hover:bg-[#FAFAFA]"}`}
                data-testid={`che-stat-${est}`}>
            <CardHeader className="pb-1">
              <CardTitle className={`text-[10px] uppercase tracking-widest font-mono flex items-center justify-between ${cls}`}>
                {lbl}
                {active && <span className="text-[9px] text-[#921A0E] normal-case tracking-normal">filtrando ×</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-xl font-heading ${cls}`}>{fmtMoney(stats[est]?.total || 0)}</div>
              <div className="text-[11px] text-neutral-500 font-mono">{stats[est]?.count || 0} cheques</div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 border border-[#E5E5E5] rounded-sm p-3 bg-white">
        <div className="flex items-center gap-2">
          <MagnifyingGlass size={14} className="text-[#999]" />
          <Input
            data-testid="filter-che-emitidos"
            placeholder="Buscar por nº, banco, proveedor o firmante…"
            value={filterQ}
            onChange={(e) => setFilterQ(e.target.value)}
            className="rounded-sm h-8 text-[12px] w-80"
          />
        </div>
        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger className="h-8 w-40 text-[12px] rounded-sm" data-testid="filter-che-emit-estado">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los estados</SelectItem>
            <SelectItem value="aceptado">Aceptado</SelectItem>
            <SelectItem value="depositado">Depositado</SelectItem>
            <SelectItem value="anulado">Anulado</SelectItem>
            <SelectItem value="rechazado">Rechazado</SelectItem>
            <SelectItem value="rescatado">Rescatado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterFirmante} onValueChange={setFilterFirmante}>
          <SelectTrigger className="h-8 w-52 text-[12px] rounded-sm" data-testid="filter-che-emit-firmante">
            <SelectValue placeholder="Firmante" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los firmantes</SelectItem>
            {firmantesLista.map((f) => (
              <SelectItem key={f.nombre} value={f.nombre}>{f.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabla */}
      <div className="border border-[#E5E5E5] rounded-sm overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] font-mono">
            <thead className="bg-[#F9F6F6] text-[10px] uppercase tracking-widest text-[#666]">
              <tr>
                <th className="px-3 py-2 text-left">Nº</th>
                <th className="px-3 py-2 text-left">Banco / chequera</th>
                <th className="px-3 py-2 text-left">Proveedor</th>
                <th className="px-3 py-2 text-left">Firmante</th>
                <th className="px-3 py-2 text-left cursor-pointer select-none hover:text-[#921A0E]" onClick={() => toggleSort("fecha_emision")} data-testid="che-sort-emision">
                  <span className="inline-flex items-center gap-1">F. Emisión <SortIcon col="fecha_emision" /></span>
                </th>
                <th className="px-3 py-2 text-left cursor-pointer select-none hover:text-[#921A0E]" onClick={() => toggleSort("fecha_cobro")} data-testid="che-sort-pago">
                  <span className="inline-flex items-center gap-1">F. Pago <SortIcon col="fecha_cobro" /></span>
                </th>
                <th className="px-3 py-2 text-right cursor-pointer select-none hover:text-[#921A0E]" onClick={() => toggleSort("monto")} data-testid="che-sort-monto">
                  <span className="inline-flex items-center gap-1 justify-end">Monto <SortIcon col="monto" /></span>
                </th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="p-4 text-center text-[#999]">Cargando…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-[#999]">
                  Sin cheques emitidos.
                </td></tr>
              )}
              {pageRows.map((c) => {
                const badge = ESTADO_BADGE[c.estado] || { label: c.estado, className: "" };
                return (
                  <tr key={c.id} data-testid={`che-emit-row-${c.numero}`}
                      className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]">
                    <td className="px-3 py-2 font-semibold">{c.numero}</td>
                    <td className="px-3 py-2 text-[11px]">
                      <span className="text-[#999] mr-1">{c.banco_code}</span>
                      {c.banco_nombre}
                      <div className="text-[10px] text-neutral-500">← {c.caja_origen_nombre}</div>
                    </td>
                    <td className="px-3 py-2 font-semibold">{c.proveedor_nombre}</td>
                    <td className="px-3 py-2 text-[11px]">
                      {c.firmante || <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-3 py-2">{fmtDate(c.fecha_emision)}</td>
                    <td className="px-3 py-2">{fmtDate(c.fecha_cobro)}</td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {fmtMoney(c.monto, c.moneda)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline"
                             className={`rounded-sm text-[10px] uppercase tracking-widest ${badge.className}`}>
                        {badge.label}
                      </Badge>
                      {c.motivo && (
                        <div className="text-[10px] text-neutral-500 mt-1 max-w-[220px] break-words">
                          {c.motivo}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-1 flex-wrap">
                        {c.estado === "aceptado" && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                                    onClick={() => setEditing(c)}
                                    data-testid={`che-btn-editar-${c.numero}`}>
                              Editar
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50"
                                    onClick={() => abrirAccion(c, "depositar")}
                                    data-testid={`che-btn-depositar-${c.numero}`}>
                              Depositar
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
                                    onClick={() => abrirAccion(c, "rescatar")}
                                    data-testid={`che-btn-rescatar-${c.numero}`}>
                              Rescatar
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                                    onClick={() => abrirAccion(c, "anular")}
                                    data-testid={`che-btn-anular-${c.numero}`}>
                              Anular
                            </Button>
                          </>
                        )}
                        {c.estado === "depositado" && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                                  onClick={() => abrirAccion(c, "rechazar")}
                                  data-testid={`che-btn-rechazar-${c.numero}`}>
                            Rechazar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Paginación */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-[#F0F0F0] text-[11px] font-mono text-[#666]" data-testid="che-emit-paginacion">
          <div className="flex items-center gap-2">
            <span>Mostrar</span>
            {[[20, "20"], [50, "50"], [0, "Todos"]].map(([n, l]) => (
              <button key={n} type="button" onClick={() => setPageSize(n)}
                      className={`px-2 py-0.5 rounded-sm border ${pageSize === n ? "bg-[#921A0E] border-[#921A0E] text-white" : "border-[#E5E5E5] hover:bg-[#FAFAFA]"}`}
                      data-testid={`che-emit-pagesize-${n}`}>
                {l}
              </button>
            ))}
            <span className="ml-2">{filtered.length} cheque{filtered.length === 1 ? "" : "s"}</span>
          </div>
          {pageSize > 0 && totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7 px-2 rounded-sm" disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)} data-testid="che-emit-page-prev">
                <CaretLeft size={12} weight="bold" />
              </Button>
              <span className="px-2" data-testid="che-emit-page-info">Página {page} de {totalPages}</span>
              <Button variant="outline" size="sm" className="h-7 px-2 rounded-sm" disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)} data-testid="che-emit-page-next">
                <CaretRight size={12} weight="bold" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <EmitirChequeDialog open={openNew} onOpenChange={setOpenNew} onSaved={load} />
      <EmitirChequeDialog open={!!editing} edit={editing}
                          onOpenChange={(o) => { if (!o) setEditing(null); }}
                          onSaved={load} />
      <AccionDialog cheque={target} accion={accion}
                    onOpenChange={(o) => { if (!o) cerrarAccion(); }}
                    onDone={load} />
    </div>
  );
}
